import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { FileCode2, Plus, Pencil, Trash2, Loader2, AlertCircle, X, Eye, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePlatformPromptTemplates,
  useCreatePromptTemplate,
  useUpdatePromptTemplate,
  useDeletePromptTemplate,
  usePreviewPromptTemplate,
  type AdminPromptTemplate,
  type TemplateInput,
} from '@/hooks/usePromptTemplates';
import { ApiError } from '@/lib/api-client';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS: Record<string, string> = {
  blog: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  review: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  niche: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  utility: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
};

export function PromptTemplatesPage() {
  const { data, isPending } = usePlatformPromptTemplates();
  const createMut = useCreatePromptTemplate();
  const updateMut = useUpdatePromptTemplate();
  const deleteMut = useDeletePromptTemplate();
  const confirm = useConfirm();

  const [editor, setEditor] = useState<{ mode: 'create' } | { mode: 'edit'; tmpl: AdminPromptTemplate } | null>(null);

  const grouped = useMemo(() => {
    if (!data) return {};
    const g: Record<string, AdminPromptTemplate[]> = {};
    for (const t of data.templates) {
      const k = t.category ?? 'other';
      (g[k] = g[k] ?? []).push(t);
    }
    return g;
  }, [data]);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileCode2 className="w-5 h-5" /> Prompt Şablonları
          </h1>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Kullanıcıların içerik üretirken kullandığı şablonlar. Placeholder'lar:
            {' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{focus_keyword}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{title_or_auto}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{tone}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{audience}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{min_words}}'}</code>-
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{max_words}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{faq_line}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{grounding_block}}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{custom_instructions_block}}'}</code>.
            {' '}Resim promptunda ek olarak{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{title}}'}</code> ve{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{excerpt}}'}</code>.
          </p>
        </div>
        <Button onClick={() => setEditor({ mode: 'create' })}>
          <Plus className="w-4 h-4" />
          Yeni Şablon
        </Button>
      </div>

      {isPending && <div className="text-muted-foreground text-sm">Yükleniyor…</div>}

      {data && Object.keys(grouped).length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            Henüz şablon yok.
          </CardContent>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, templates]) => (
        <div key={cat} className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            {cat === 'blog' ? 'Blog' : cat === 'review' ? 'İnceleme' : cat === 'niche' ? 'Nişe Özel' : cat === 'utility' ? 'Yardımcı' : cat}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((t) => (
              <Card key={t.id} className={!t.enabled ? 'opacity-60' : undefined}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <span>{t.label}</span>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{t.id}</code>
                    {!!t.is_system && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Sistem</span>
                    )}
                    {!t.enabled && (
                      <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">Kapalı</span>
                    )}
                    {t.category && (
                      <span className={cn('text-xs px-2 py-0.5 rounded', CATEGORY_COLORS[t.category] ?? 'bg-muted')}>
                        {t.category}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>#{t.sort_order}</span>
                    {t.image_prompt_template && (
                      <span className="inline-flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        Görsel
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditor({ mode: 'edit', tmpl: t })}>
                      <Pencil className="w-3.5 h-3.5" />
                      Düzenle
                    </Button>
                    {!!t.enabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          const action = t.is_system ? 'pasifleştir' : 'sil';
                          const ok = await confirm({
                            title: `Şablonu ${action}?`,
                            description: t.is_system
                              ? `"${t.label}" sistem şablonu pasifleştirilecek — kullanıcı dropdown'ında görünmez. Sonra tekrar aktifleştirebilirsin.`
                              : `"${t.label}" şablonu kalıcı olarak silinecek.`,
                            confirmText: t.is_system ? 'Pasifleştir' : 'Sil',
                            danger: true,
                          });
                          if (!ok) return;
                          deleteMut.mutate(t.id);
                        }}
                        disabled={deleteMut.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t.is_system ? 'Pasifleştir' : 'Sil'}
                      </Button>
                    )}
                    {!t.enabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateMut.mutate({ id: t.id, enabled: true })}
                      >
                        Aktifleştir
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {editor && (
        <TemplateEditor
          initial={editor.mode === 'edit' ? editor.tmpl : null}
          onClose={() => setEditor(null)}
          onSubmit={async (body) => {
            if (editor.mode === 'create') {
              await createMut.mutateAsync(body);
            } else {
              const { id: _drop, ...patch } = body;
              await updateMut.mutateAsync({ id: editor.tmpl.id, ...patch });
            }
            setEditor(null);
          }}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}
    </div>
  );
}

function TemplateEditor({
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  initial: AdminPromptTemplate | null;
  onClose: () => void;
  onSubmit: (body: TemplateInput) => Promise<void>;
  submitting: boolean;
}) {
  const [id, setId] = useState(initial?.id ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'blog');
  const [icon, setIcon] = useState(initial?.icon ?? 'FileText');
  const [systemPrompt, setSystemPrompt] = useState(initial?.system_prompt ?? '');
  const [userPrompt, setUserPrompt] = useState(initial?.user_prompt_template ?? '');
  const [imagePrompt, setImagePrompt] = useState(initial?.image_prompt_template ?? '');
  const [imageStyleSuffix, setImageStyleSuffix] = useState(initial?.image_style_suffix ?? '');
  const [defaultSettingsJson, setDefaultSettingsJson] = useState(
    initial?.default_settings_json ?? '{"tone":"Profesyonel","audience":"Genel okuyucu","section_length":"medium","include_faq":false}',
  );
  const [sortOrder, setSortOrder] = useState<string>(initial ? initial.sort_order.toString() : '100');
  const [enabled, setEnabled] = useState<boolean>(initial ? !!initial.enabled : true);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const previewMut = usePreviewPromptTemplate();

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const runPreview = async () => {
    if (!initial) {
      setError('Önizleme için önce şablonu kaydet');
      return;
    }
    setError(null);
    try {
      await previewMut.mutateAsync({
        id: initial.id,
        vars: {
          focus_keyword: 'örnek anahtar kelime',
          tone: 'Profesyonel',
          audience: 'Genel okuyucu',
          target_words: 800,
          include_faq: false,
        },
      });
      setShowPreview(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Önizleme başarısız');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (defaultSettingsJson.trim()) {
      try {
        JSON.parse(defaultSettingsJson);
      } catch {
        setError('Default ayarlar JSON geçersiz');
        return;
      }
    }
    try {
      await onSubmit({
        id,
        label,
        description,
        category,
        icon,
        system_prompt: systemPrompt,
        user_prompt_template: userPrompt,
        image_prompt_template: imagePrompt,
        image_style_suffix: imageStyleSuffix,
        default_settings_json: defaultSettingsJson,
        sort_order: parseInt(sortOrder, 10) || 100,
        enabled,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card className="w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 sticky top-0 bg-card z-10 border-b">
          <CardTitle>
            {initial ? `${initial.label} — Düzenle` : 'Yeni Şablon'}
            {initial?.is_system ? (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-normal">Sistem</span>
            ) : null}
          </CardTitle>
          <div className="flex gap-2">
            {initial && (
              <Button type="button" variant="outline" size="sm" onClick={runPreview} disabled={previewMut.isPending}>
                {previewMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                Önizleme
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <form onSubmit={submit}>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tid">ID *</Label>
                <Input
                  id="tid"
                  value={id}
                  onChange={(e) => setId(e.target.value.toLowerCase())}
                  placeholder="my_template"
                  required
                  disabled={!!initial}
                />
                <p className="text-xs text-muted-foreground">lowercase, a-z, 0-9, _</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tlabel">Görünen Ad *</Label>
                <Input id="tlabel" value={label} onChange={(e) => setLabel(e.target.value)} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tdesc">Açıklama (kullanıcılara görünür)</Label>
              <Input id="tdesc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tcat">Kategori</Label>
                <select
                  id="tcat"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="blog">Blog</option>
                  <option value="review">İnceleme</option>
                  <option value="niche">Nişe Özel</option>
                  <option value="utility">Yardımcı</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticon">Icon (lucide adı)</Label>
                <Input id="ticon" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="FileText" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tsort">Sıralama</Label>
                <Input
                  id="tsort"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsys">System Prompt *</Label>
              <textarea
                id="tsys"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={4}
                required
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                placeholder="Sen {{language}} yazan, SEO konusunda uzman bir yazarsın..."
              />
              <p className="text-xs text-muted-foreground">AI'a verilecek system role mesajı. Placeholder kullanabilirsin.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tusr">User Prompt Template *</Label>
              <textarea
                id="tusr"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={14}
                required
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Kullanıcı girdileri yerleştirilecek ana prompt. <code>{'{{focus_keyword}}'}</code>, <code>{'{{tone}}'}</code>,
                <code>{'{{target_words}}'}</code>, <code>{'{{grounding_block}}'}</code>, <code>{'{{custom_instructions_block}}'}</code> vb.
                Çıktının SADECE JSON olduğundan emin ol.
              </p>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Featured Image (opsiyonel)</h3>

              <div className="space-y-2">
                <Label htmlFor="timg">Image Prompt Template</Label>
                <textarea
                  id="timg"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  placeholder='Editorial header image about "{{focus_keyword}}"...'
                />
                <p className="text-xs text-muted-foreground">
                  Boş bırakırsan kullanıcı elle prompt yazar. Placeholder: <code>{'{{focus_keyword}}'}</code>,{' '}
                  <code>{'{{title}}'}</code>, <code>{'{{excerpt}}'}</code>.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tstyle">Image Style Suffix</Label>
                <Input
                  id="tstyle"
                  value={imageStyleSuffix}
                  onChange={(e) => setImageStyleSuffix(e.target.value)}
                  placeholder="photorealistic, editorial, clean composition"
                />
                <p className="text-xs text-muted-foreground">Image prompt sonuna ". Style: ..." olarak eklenir.</p>
              </div>
            </div>

            <div className="border-t pt-4 space-y-2">
              <Label htmlFor="tdef">Default Ayarlar (JSON)</Label>
              <textarea
                id="tdef"
                value={defaultSettingsJson}
                onChange={(e) => setDefaultSettingsJson(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                placeholder='{"tone":"Profesyonel","audience":"Genel okuyucu","section_length":"medium","include_faq":false}'
              />
              <p className="text-xs text-muted-foreground">Kullanıcı formu açıldığında önerilecek ayarlar.</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="tenabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="tenabled" className="cursor-pointer">
                Aktif (kullanıcılar seçebilir)
              </Label>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {showPreview && previewMut.data && (
              <div className="border rounded-md p-3 bg-muted/30 space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Önizleme (örnek değişkenlerle)</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">System:</div>
                  <pre className="whitespace-pre-wrap bg-background p-2 rounded border">{previewMut.data.system_prompt}</pre>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">User:</div>
                  <pre className="whitespace-pre-wrap bg-background p-2 rounded border">{previewMut.data.user_prompt}</pre>
                </div>
                {previewMut.data.image_prompt && (
                  <div>
                    <div className="text-muted-foreground mb-1">Image:</div>
                    <pre className="whitespace-pre-wrap bg-background p-2 rounded border">{previewMut.data.image_prompt}</pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
          <div className="flex justify-end gap-2 border-t px-6 py-4 sticky bottom-0 bg-card">
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Kaydet
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
